(() => {
  if (customElements.get("brand-portal")) {
    return;
  }

  class BrandPortal extends HTMLElement {
    connectedCallback() {
      this.sections = Array.from(this.querySelectorAll("[data-brand-section]"));
      this.images = Array.from(this.querySelectorAll("[data-brand-image]"));

      this.activeIndex = -1;
      this.scrollRafId = null;
      this.onScroll = this.requestScrollUpdate.bind(this);
      this.onResize = this.handleResize.bind(this);
      this.lastScrollY = window.scrollY;
      this.scrollDirection = "down";
      this.triggerRatio = 0.18;

      if (!this.sections.length) {
        return;
      }

      this.updateActive(0);
      this.handleScroll();

      window.addEventListener("scroll", this.onScroll, { passive: true });
      window.addEventListener("resize", this.onResize);
    }

    disconnectedCallback() {
      if (this.scrollRafId) {
        window.cancelAnimationFrame(this.scrollRafId);
        this.scrollRafId = null;
      }
      this.clearSectionTimeouts();
      window.removeEventListener("scroll", this.onScroll);
      window.removeEventListener("resize", this.onResize);
    }

    clearSectionTimeouts() {
      if (!this.sections || !this.sections.length) {
        return;
      }

      this.sections.forEach((section) => {
        if (section.exitTimeout) {
          window.clearTimeout(section.exitTimeout);
          section.exitTimeout = null;
        }
      });
    }

    requestScrollUpdate() {
      if (this.scrollRafId) {
        return;
      }

      this.scrollRafId = window.requestAnimationFrame(() => {
        this.scrollRafId = null;
        this.handleScroll();
      });
    }

    handleScroll() {
      if (!this.sections || !this.sections.length) {
        return;
      }

      const currentScrollY = window.scrollY;
      if (currentScrollY < this.lastScrollY) {
        this.scrollDirection = "up";
      } else if (currentScrollY > this.lastScrollY) {
        this.scrollDirection = "down";
      }
      this.lastScrollY = currentScrollY;

      if (currentScrollY <= 2) {
        this.sections.forEach((section) => {
          section.classList.remove("is-exiting");
          section.classList.remove("skip-enter");
          if (section.exitTimeout) {
            window.clearTimeout(section.exitTimeout);
            section.exitTimeout = null;
          }
        });
      }

      const viewportCenter = window.innerHeight / 2;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.sections.forEach((section, index) => {
        const rect = section.getBoundingClientRect();
        const sectionCenter = rect.top + rect.height / 2;
        const distance = Math.abs(sectionCenter - viewportCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      this.updateActive(closestIndex, this.scrollDirection);

      const activeSection = this.sections[this.activeIndex];
      if (!activeSection) {
        return;
      }

      if (this.scrollDirection === "down") {
        activeSection.classList.remove("skip-enter");
      } else {
        activeSection.classList.add("skip-enter");
        activeSection.classList.remove("is-exiting");
      }

      if (this.scrollDirection === "up") {
        activeSection.classList.remove("is-exiting");
        return;
      }

      const typography = activeSection.querySelector("[data-brand-typography]");
      if (!typography) {
        return;
      }

      if (this.activeIndex === this.sections.length - 1) {
        activeSection.classList.remove("is-exiting");
        return;
      }

      const triggerY = window.innerHeight * this.triggerRatio;
      const typographyTop = typography.getBoundingClientRect().top;
      activeSection.classList.toggle("is-exiting", typographyTop <= triggerY);
    }

    handleResize() {
      this.requestScrollUpdate();
    }

    updateActive(index, direction = this.scrollDirection) {
      if (this.activeIndex === index) {
        return;
      }

      const previousIndex = this.activeIndex;
      this.activeIndex = index;

      if (previousIndex >= 0 && previousIndex !== index) {
        const previousSection = this.sections[previousIndex];
        if (direction === "down") {
          previousSection.classList.add("is-exiting");
          if (previousSection.exitTimeout) {
            window.clearTimeout(previousSection.exitTimeout);
          }
          previousSection.exitTimeout = window.setTimeout(() => {
            previousSection.classList.remove("is-exiting");
            previousSection.exitTimeout = null;
          }, 700);
        } else {
          previousSection.classList.remove("is-exiting");
        }
      }

      this.sections.forEach((section, sectionIndex) => {
        section.classList.toggle("skip-enter", direction === "up");
        section.classList.remove("is-exiting");
        if (sectionIndex === index) {
          section.classList.remove("is-exiting");
        }
        section.classList.toggle("is-active", sectionIndex === index);
      });

      this.images.forEach((image, imageIndex) => {
        image.classList.toggle("is-active", imageIndex === index);
      });
    }
  }

  customElements.define("brand-portal", BrandPortal);
})();
